"use client";

import React, { useEffect, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import Skeleton from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { getAllUserPhotos, supabase, updateProfileAvatarUrl } from '@/lib/supabase';
import { getCroppedImg } from '@/lib/imageUtils';
import Cropper from 'react-easy-crop';
import { Loader2, Image as ImageIcon, ArrowLeft, Crop as CropIcon, CheckCircle2 } from '@/components/icons';

interface AvatarUploadNavbarProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
  currentAvatarUrl: string | null | undefined;
  onAvatarUpdated: () => void;
}

const AvatarUploadNavbar: React.FC<AvatarUploadNavbarProps> = ({
  isOpen,
  onClose,
  session,
  currentAvatarUrl,
  onAvatarUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null | undefined>(currentAvatarUrl);
  const [photos, setPhotos] = useState<string[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosVisibleCount, setPhotosVisibleCount] = useState(12);
  const [selectedGalleryPhotoForPreview, setSelectedGalleryPhotoForPreview] = useState<string | null>(null);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const { toast } = useToast();
  const hasSelectedPhoto = Boolean(selectedGalleryPhotoForPreview);
  const selectedPhotoIsCurrent = Boolean(selectedGalleryPhotoForPreview && previewAvatarUrl && selectedGalleryPhotoForPreview === previewAvatarUrl);

  useEffect(() => {
    setPreviewAvatarUrl(currentAvatarUrl);
  }, [currentAvatarUrl]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedGalleryPhotoForPreview(null);
      setImageToCrop(null);
      setPhotosVisibleCount(12);
    }
  }, [isOpen]);

  useEffect(() => {
    let active = true;

    const loadPhotos = async () => {
      if (!isOpen || !session?.user?.id) return;
      setPhotosLoading(true);
      try {
        const userPhotos = await getAllUserPhotos(session.user.id);
        if (!active) return;
        setPhotos(userPhotos);
        setPhotosVisibleCount(12);

        if (currentAvatarUrl && userPhotos.includes(currentAvatarUrl)) {
          setSelectedGalleryPhotoForPreview(currentAvatarUrl);
        } else if (userPhotos.length > 0) {
          setSelectedGalleryPhotoForPreview(userPhotos[0]);
        } else {
          setSelectedGalleryPhotoForPreview(null);
        }
      } catch (error) {
        console.error('Error loading user photos:', error);
        if (active) {
          setPhotos([]);
          toast({
            title: 'Error',
            description: 'No se pudieron cargar tus fotos anteriores.',
            variant: 'destructive',
          });
        }
      } finally {
        if (active) setPhotosLoading(false);
      }
    };

    loadPhotos();

    return () => {
      active = false;
    };
  }, [isOpen, session?.user?.id, currentAvatarUrl, toast]);

  const handleUpdateAvatar = async (source: File | string | null) => {
    if (!session?.user?.id) return;
    setLoading(true);

    try {
      let newAvatarUrl: string | null = null;

      if (source === null) {
        setPreviewAvatarUrl(null);
        if (currentAvatarUrl) {
          const pathSegments = currentAvatarUrl.split('/');
          const fileName = pathSegments[pathSegments.length - 1];
          const folderName = pathSegments[pathSegments.length - 2];
          if (folderName === session.user.id) {
            await supabase.storage.from('avatars').remove([`${session.user.id}/${fileName}`]);
          }
        }
      } else if (typeof source === 'string') {
        newAvatarUrl = source;
        setPreviewAvatarUrl(source);
      } else {
        setPreviewAvatarUrl(URL.createObjectURL(source));
        const { data, error } = await supabase.storage
          .from('avatars')
          .upload(`${session.user.id}/avatar_${Date.now()}.jpeg`, source, {
            cacheControl: '3600',
            upsert: true,
            contentType: 'image/jpeg',
          });

        if (error) {
          console.error('Error uploading cropped avatar:', error);
          throw error;
        }

        const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(data.path);
        newAvatarUrl = publicUrlData.publicUrl;
        setPreviewAvatarUrl(newAvatarUrl);
      }

      await updateProfileAvatarUrl(session.user.id, newAvatarUrl);
      setPreviewAvatarUrl(newAvatarUrl);
      onAvatarUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating avatar URL in DB:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la URL del avatar. Intentalo de nuevo.',
        variant: 'destructive',
      });
      setPreviewAvatarUrl(currentAvatarUrl);
    } finally {
      setLoading(false);
      setSelectedGalleryPhotoForPreview(null);
      setImageToCrop(null);
    }
  };

  const handleCropApply = async () => {
    if (!imageToCrop || !croppedAreaPixels) {
      toast({
        title: 'Recorte incompleto',
        description: 'Primero ajusta el area de recorte.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const croppedImageBlob = await getCroppedImg(imageToCrop, croppedAreaPixels, rotation);
      if (!croppedImageBlob) {
        throw new Error('No se pudo recortar la imagen');
      }

      const croppedImageFile = new File([croppedImageBlob], `avatar_${Date.now()}.jpeg`, { type: 'image/jpeg' });
      await handleUpdateAvatar(croppedImageFile);
    } catch (error) {
      console.error('Error while cropping image:', error);
      toast({
        title: 'Error de recorte',
        description: 'No se pudo recortar la imagen. Intentalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearAvatar = () => {
    if (!session?.user?.id) return;

    setLoading(true);
    setPreviewAvatarUrl(null);

    updateProfileAvatarUrl(session.user.id, null)
      .then(() => onAvatarUpdated())
      .catch((error) => {
        console.error('Error clearing avatar URL in DB:', error);
        toast({
          title: 'Error',
          description: 'No se pudo quitar la foto del perfil. Intentalo de nuevo.',
          variant: 'destructive',
        });
        setPreviewAvatarUrl(currentAvatarUrl);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUseGalleryPhotoDirectly = () => {
    if (selectedGalleryPhotoForPreview) {
      handleUpdateAvatar(selectedGalleryPhotoForPreview);
    }
  };

  const handleCropGalleryPhoto = () => {
    if (selectedGalleryPhotoForPreview) {
      setImageToCrop(selectedGalleryPhotoForPreview);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    }
  };

  const handleCancelGallerySelection = () => {
    setSelectedGalleryPhotoForPreview(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const tempUrl = URL.createObjectURL(file);
      setImageToCrop(tempUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  return (
    <>
      <div
        className={cn(
          'absolute inset-0 bg-background z-20 flex flex-col transition-transform duration-200',
          isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        )}
        aria-hidden={!isOpen}
      >
        <div className="flex items-center gap-2 p-4 border-b bg-primary text-primary-foreground min-h-[64px]">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10"
            onClick={onClose}
            disabled={loading}
            aria-label="Volver"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h3 className="text-lg font-semibold leading-none">
            {imageToCrop ? 'Recortar foto' : 'Cambiar foto'}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-6">
            {imageToCrop ? (
              <div className="space-y-4">
                <div className="rounded-xl border bg-card p-3">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold">Editor de recorte</p>
                      <p className="text-xs text-muted-foreground">La mascara circular muestra como se vera tu avatar.</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCrop({ x: 0, y: 0 });
                        setZoom(1);
                        setRotation(0);
                      }}
                      disabled={loading}
                    >
                      Restablecer
                    </Button>
                  </div>
                </div>

                <div className="relative w-full aspect-square min-h-[280px] rounded-md overflow-hidden bg-muted">
                  <Cropper
                    image={imageToCrop}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={1 / 1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onRotationChange={setRotation}
                    onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                    cropShape="round"
                    showGrid={false}
                    minZoom={1}
                    maxZoom={10}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="avatar-zoom">Zoom</Label>
                  <Slider
                    id="avatar-zoom"
                    min={1}
                    max={10}
                    step={0.1}
                    value={[zoom]}
                    onValueChange={(val) => setZoom(val[0])}
                    disabled={loading}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="avatar-rotation">Rotacion</Label>
                  <Slider
                    id="avatar-rotation"
                    min={0}
                    max={360}
                    step={1}
                    value={[rotation]}
                    onValueChange={(val) => setRotation(val[0])}
                    disabled={loading}
                  />
                </div>
              </div>
            ) : (
              <>
                {selectedGalleryPhotoForPreview && (
                  <div className="rounded-2xl border bg-card p-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold">Foto seleccionada</h4>
                        <p className="text-xs text-muted-foreground">
                          {selectedPhotoIsCurrent ? 'Es tu avatar actual. Puedes recortarla otra vez si quieres.' : 'Esta sera tu nueva foto de perfil si la guardas.'}
                        </p>
                      </div>
                      {!selectedPhotoIsCurrent && <Badge>Nueva</Badge>}
                    </div>

                    <img src={selectedGalleryPhotoForPreview} alt="Foto seleccionada" loading="lazy" decoding="async" className="w-full h-52 object-contain rounded-xl border bg-muted/30" />

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleCropGalleryPhoto}
                        disabled={loading || !hasSelectedPhoto}
                        className="flex-1"
                      >
                        <CropIcon className="mr-2 h-4 w-4" /> Recortar
                      </Button>
                      <Button variant="ghost" onClick={handleClearAvatar} disabled={loading || !previewAvatarUrl} className="flex-1">
                        Eliminar
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-semibold">Tus fotos anteriores</h4>
                      <p className="text-xs text-muted-foreground">Toca una miniatura para verla arriba y usarla como avatar.</p>
                    </div>
                    {photos.length > 0 && <Badge variant="secondary">{photos.length} fotos</Badge>}
                  </div>

                  {photosLoading ? (
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={index} className="aspect-square rounded-xl" />
                      ))}
                    </div>
                  ) : photos.length > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        {photos.slice(0, photosVisibleCount).map((photoUrl) => (
                          <button
                            key={photoUrl}
                            type="button"
                            className={cn(
                              'relative aspect-square rounded-xl overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary/30',
                              selectedGalleryPhotoForPreview === photoUrl ? 'border-primary shadow-md scale-[0.98]' : 'border-transparent hover:border-primary/30'
                            )}
                            onClick={() => setSelectedGalleryPhotoForPreview(photoUrl)}
                            disabled={loading}
                          >
                            <img src={photoUrl} alt="Foto anterior" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            {selectedGalleryPhotoForPreview === photoUrl && (
                              <div className="absolute inset-0 bg-primary/15 flex items-start justify-end p-2">
                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                                  <CheckCircle2 className="h-4 w-4" />
                                </span>
                              </div>
                            )}
                            {currentAvatarUrl === photoUrl && (
                              <div className="absolute left-2 bottom-2">
                                <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">Actual</Badge>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>

                      {photosVisibleCount < photos.length && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setPhotosVisibleCount((prev) => Math.min(prev + 12, photos.length))}
                          disabled={loading}
                          className="w-full"
                        >
                          Cargar mas fotos
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed p-4 text-center bg-muted/20">
                      <p className="text-sm font-medium">No hay fotos anteriores todavia.</p>
                      <p className="text-xs text-muted-foreground mt-1">Sube una foto desde el boton inferior para empezar.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="border-t p-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 space-y-2">
          {imageToCrop ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setImageToCrop(null)}
                disabled={loading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button onClick={handleCropApply} disabled={loading} className="flex-1">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Guardar recorte'}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Button
                  variant="default"
                  onClick={handleUseGalleryPhotoDirectly}
                  disabled={loading || !hasSelectedPhoto || selectedPhotoIsCurrent}
                  className="w-full"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : selectedPhotoIsCurrent ? 'Foto actual' : 'Guardar foto'}
                </Button>
              </div>

              <Button
                variant="secondary"
                onClick={() => document.getElementById('avatar-file-input-navbar')?.click()}
                disabled={loading}
                className="w-full"
              >
                <ImageIcon className="mr-2 h-4 w-4" /> Seleccionar foto nueva
              </Button>
              <input
                id="avatar-file-input-navbar"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={loading}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AvatarUploadNavbar;
